import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { deliveryApi, farmApi, stableApi, boxApi, documentApi, contractApi, API_BASE_URL } from '../services/api';
import { Delivery, Farm, Animal, Stable, Box, ContractTemplate } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import AnimalSelectorModal, { SelectedAnimal, AnimalWarning, warningBadge } from '../components/AnimalSelectorModal';
import Pagination from '../components/Pagination';
import ClearableInput from '../components/ClearableInput';
import ClearableSelect from '../components/ClearableSelect';
import ConfirmModal from '../components/ConfirmModal';
import MasterDataCombobox from '../components/MasterDataCombobox';

const Deliveries: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('outgoing'); // 'incoming' or 'outgoing'
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [stables, setStables] = useState<Stable[]>([]);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAnimalModal, setShowAnimalModal] = useState(false);
  const [showViewAnimalsModal, setShowViewAnimalsModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState<Delivery | null>(null);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [farmFilter, setFarmFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [deliveryDocuments, setDeliveryDocuments] = useState<any[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocumentType, setUploadDocumentType] = useState<string>('health_certificate');
  const [uploadDescription, setUploadDescription] = useState<string>('');
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [contractTemplates, setContractTemplates] = useState<ContractTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ action: string; id: number; extra?: string } | null>(null);
  const [exportingDeliveryIds, setExportingDeliveryIds] = useState<Set<number>>(new Set());

  // Warning confirmation for adding animals with issues
  const [pendingAnimals, setPendingAnimals] = useState<SelectedAnimal[] | null>(null);
  const [showWarningConfirm, setShowWarningConfirm] = useState(false);

  const [formData, setFormData] = useState({
    farm_id: '',
    type: 'outgoing' as 'incoming' | 'outgoing',
    delivery_date: '',
    seller: '',
    transport_company: '',
    total_transport_cost: '',
    invoice_number: '',
    contract_number: '',
    notes: ''
  });

  // Load reference data once on mount
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [farmsResponse, templatesResponse] = await Promise.all([
          farmApi.getAll(),
          contractApi.getTemplates()
        ]);

        setFarms(farmsResponse);
        setContractTemplates(templatesResponse);

        // Load stables and boxes for all farms
        const allStables: Stable[] = [];
        const allBoxes: Box[] = [];

        for (const farm of farmsResponse) {
          try {
            const [farmStables, farmBoxes] = await Promise.all([
              stableApi.getAll(farm.id),
              boxApi.getAll(farm.id)
            ]);
            allStables.push(...farmStables);
            allBoxes.push(...farmBoxes);
          } catch (error) {
            console.error(`Error loading data for farm ${farm.id}:`, error);
            handleApiError(error, 'Hiba a telepadatok betöltésekor');
          }
        }

        setStables(allStables);
        setBoxes(allBoxes);
      } catch (error) {
        console.error('Error loading reference data:', error);
        handleApiError(error, 'Hiba az adatok betöltésekor');
      }
    };

    loadReferenceData();
  }, []);

  // Debounce search term (300ms, matching Animals.tsx pattern)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load deliveries when filters/page/tab/search change.
  // Reset to page 1 when filters change (without double-fetching).
  const prevDeps = useRef({ debouncedSearch, farmFilter, activeTab });
  const isInitialMount = useRef(true);
  useEffect(() => {
    const filtersChanged = !isInitialMount.current && (
      prevDeps.current.debouncedSearch !== debouncedSearch ||
      prevDeps.current.farmFilter !== farmFilter ||
      prevDeps.current.activeTab !== activeTab
    );
    prevDeps.current = { debouncedSearch, farmFilter, activeTab };
    isInitialMount.current = false;

    if (filtersChanged && page !== 1) {
      setPage(1); // Will re-trigger this effect with page 1
      return;
    }

    loadDeliveries();
  }, [farmFilter, page, activeTab, debouncedSearch]);

  const loadDeliveries = async () => {
    try {
      setLoadingDeliveries(true);
      const params: { farm_id?: number; page?: number; type?: string; search?: string } = { page, type: activeTab };
      if (farmFilter) params.farm_id = parseInt(farmFilter);
      if (debouncedSearch) params.search = debouncedSearch;
      const response = await deliveryApi.getAll(params);
      setDeliveries(response.data);
      setLastPage(response.last_page);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading deliveries:', error);
      handleApiError(error, 'Hiba a szállítólevelek betöltésekor');
    } finally {
      setLoading(false);
      setLoadingDeliveries(false);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const deliveryData = {
        ...formData,
        farm_id: parseInt(formData.farm_id),
        total_transport_cost: formData.total_transport_cost ? parseFloat(formData.total_transport_cost) : undefined
      };

      if (editingDelivery) {
        await deliveryApi.update(editingDelivery.id, deliveryData);
        showSuccess(t('delivery_updated_success', 'deliveries'));
      } else {
        await deliveryApi.create(deliveryData);
        showSuccess(t('delivery_created_success', 'deliveries'));
      }

      setShowModal(false);
      setEditingDelivery(null);
      resetForm();
      loadDeliveries();
    } catch (error: any) {
      console.error('Error saving delivery:', error);
      handleApiError(error, 'Hiba a szállítólevél mentésekor');
    }
  };

  const handleEdit = (delivery: Delivery) => {
    setEditingDelivery(delivery);
    setFormData({
      farm_id: delivery.farm_id.toString(),
      type: delivery.type,
      delivery_date: delivery.delivery_date ? delivery.delivery_date.split('T')[0] : '',
      seller: delivery.seller,
      transport_company: delivery.transport_company || '',
      total_transport_cost: delivery.total_transport_cost?.toString() || '',
      invoice_number: delivery.invoice_number || '',
      contract_number: delivery.contract_number || '',
      notes: delivery.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deliveryApi.delete(id);
      showSuccess(t('delivery_deleted_success', 'deliveries'));
      loadDeliveries();
    } catch (error) {
      console.error('Error deleting delivery:', error);
      handleApiError(error, 'Hiba a szállítólevél törlésekor');
    }
  };

  const handleGenerateContract = async () => {
    if (!selectedDelivery || !selectedTemplateId) {
      showError('Kérlek válassz egy sablont!');
      return;
    }

    try {
      const blob = await contractApi.generateContract(selectedDelivery.id, selectedTemplateId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `szerzodes_${selectedDelivery.seller}_${selectedDelivery.delivery_date}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setShowContractModal(false);
      setSelectedTemplateId(null);
      showSuccess('Szerződés sikeresen generálva és letöltve!');
    } catch (error: any) {
      console.error('Error generating contract:', error);
      handleApiError(error, 'Hiba a szerződés generálásakor');
    }
  };

  const openContractModal = (delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setShowContractModal(true);
  };

  // Map delivery type to template type
  const getTemplateType = (deliveryType: 'incoming' | 'outgoing'): 'sale' | 'purchase' => {
    return deliveryType === 'incoming' ? 'purchase' : 'sale';
  };

  const handleAddAnimalsConfirmed = async (animalIds: number[]) => {
    if (!selectedDelivery || animalIds.length === 0) {
      showError(t('select_animals_to_add', 'deliveries'));
      return;
    }

    try {
      const response = await deliveryApi.addAnimals(selectedDelivery.id, animalIds);
      showSuccess(response.message || t('animals_added_success', 'deliveries'));
      setShowAnimalModal(false);
      setSelectedDelivery(null);
      loadDeliveries();
    } catch (error: any) {
      console.error('Error adding animals:', error);
      handleApiError(error, 'Hiba az állatok hozzáadásakor');
    }
  };

  const openViewAnimalsModal = async (delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setShowViewAnimalsModal(true);
    // Fetch fresh delivery data with warnings from show endpoint
    if (delivery.type === 'outgoing' && delivery.status !== 'completed') {
      try {
        const freshDelivery = await deliveryApi.getById(delivery.id);
        setSelectedDelivery(freshDelivery);
      } catch (error) {
        console.error('Error loading delivery details:', error);
      }
    }
  };

  const handleAnimalSelection = (animals: SelectedAnimal[]) => {
    const animalsWithWarnings = animals.filter(a => a.delivery_warnings && a.delivery_warnings.length > 0);
    if (animalsWithWarnings.length > 0) {
      setPendingAnimals(animals);
      setShowWarningConfirm(true);
    } else {
      handleAddAnimalsConfirmed(animals.map(a => a.id));
    }
  };

  const handlePreCheckComplete = async (deliveryId: number) => {
    // Pre-check: fetch fresh delivery data and check for already-shipped animals
    try {
      const freshDelivery = await deliveryApi.getById(deliveryId);
      const shippedAnimals = (freshDelivery.animals || []).filter((a: any) =>
        a.warnings && a.warnings.some((w: AnimalWarning) => w.type === 'already_shipped')
      );

      if (shippedAnimals.length > 0) {
        const earTags = shippedAnimals.map((a: any) => a.ear_tag).join(', ');
        showError(
          `A szállítás nem fejezhető be. Az alábbi állatok már ki lettek szállítva egy másik szállítmányban: ${earTags}. Távolítsd el őket a szállításból, majd próbáld újra.`,
          'dialog'
        );
        return;
      }

      // No conflicts — show the standard confirmation modal
      setConfirmAction({ action: 'completeDelivery', id: deliveryId });
    } catch (error) {
      console.error('Error pre-checking delivery:', error);
      handleApiError(error, 'Hiba a szállítás ellenőrzésekor');
    }
  };

  const handleCompleteDelivery = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/deliveries/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status: 'completed' }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (errorData?.conflicting_animals) {
          const earTags = errorData.conflicting_animals.map((a: any) => a.ear_tag).join(', ');
          showError(
            `A szállítás nem fejezhető be. Ütköző állatok: ${earTags}`,
            'dialog'
          );
          return;
        }
        throw new Error(errorData?.message || 'Failed to complete delivery');
      }

      showSuccess('Szállítás befejezettként jelölve');
      loadDeliveries();
    } catch (error) {
      console.error('Error completing delivery:', error);
      handleApiError(error, 'Hiba a szállítás befejezésekor');
    }
  };

  const resetForm = () => {
    setFormData({
      farm_id: '',
      type: activeTab as 'incoming' | 'outgoing',
      delivery_date: '',
      seller: '',
      transport_company: '',
      total_transport_cost: '',
      invoice_number: '',
      contract_number: '',
      notes: ''
    });
  };

  const filteredDeliveries = deliveries;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF'
    }).format(amount);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const loadDeliveryDocuments = async (deliveryId: number) => {
    try {
      setLoadingDocuments(true);
      const response = await deliveryApi.getDeliveryDocuments(deliveryId);
      setDeliveryDocuments(response.documents || []);
    } catch (error) {
      console.error('Error loading delivery documents:', error);
      handleApiError(error, 'Hiba a dokumentumok betöltésekor');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleOpenDocumentsModal = async (delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setShowDocumentsModal(true);
    await loadDeliveryDocuments(delivery.id);
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !selectedDelivery) {
      showError('Kérlek válassz ki egy fájlt!');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', uploadDocumentType);
      if (uploadDescription) {
        formData.append('description', uploadDescription);
      }

      await deliveryApi.uploadDeliveryDocument(selectedDelivery.id, formData);
      showSuccess('Dokumentum sikeresen feltöltve!');

      setUploadFile(null);
      setUploadDocumentType('health_certificate');
      setUploadDescription('');

      await loadDeliveryDocuments(selectedDelivery.id);
    } catch (error: any) {
      console.error('Error uploading document:', error);
      handleApiError(error, 'Hiba a dokumentum feltöltésekor');
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await documentApi.deleteDocument(documentId);
      showSuccess('Dokumentum sikeresen törölve!');
      if (selectedDelivery) {
        await loadDeliveryDocuments(selectedDelivery.id);
      }
    } catch (error: any) {
      console.error('Error deleting document:', error);
      handleApiError(error, 'Hiba a dokumentum törlésekor');
    }
  };

  const handleRemoveAnimal = async (animalId: number) => {
    if (!selectedDelivery) return;
    try {
      const response = await fetch(`${API_BASE_URL}/deliveries/${selectedDelivery.id}/animals`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ animal_ids: [animalId] }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove animal');
      }

      const data = await response.json();
      showSuccess(data.message);

      // Reload deliveries list and refresh the modal's delivery data
      loadDeliveries();
      const freshDelivery = await deliveryApi.getById(selectedDelivery.id);
      setSelectedDelivery(freshDelivery);
    } catch (error) {
      console.error('Error removing animal:', error);
      handleApiError(error, 'Hiba az állat eltávolításakor');
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { action, id } = confirmAction;

    try {
      if (action === 'deleteDelivery') await handleDelete(id);
      else if (action === 'completeDelivery') await handleCompleteDelivery(id);
      else if (action === 'deleteDocument') await handleDeleteDocument(id);
      else if (action === 'removeAnimal') await handleRemoveAnimal(id);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleDownloadDocument = async (documentId: number, filename: string) => {
    try {
      const blob = await documentApi.downloadDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading document:', error);
      handleApiError(error, 'Hiba a dokumentum letöltésekor');
    }
  };

  const handleExportDeliveryAnimals = async (delivery: Delivery) => {
    setExportingDeliveryIds(prev => {
      const next = new Set(prev);
      next.add(delivery.id);
      return next;
    });
    try {
      const { blob, filename, exportCount } = await deliveryApi.exportAnimals(delivery.id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (exportCount === 0) {
        showError('Ehhez a szállításhoz nincsenek állatok. Az export fájl üres.');
      } else {
        showSuccess('Exportálás sikeres!');
      }
    } catch (error) {
      console.error('Error exporting delivery animals:', error);
      handleApiError(error, 'Hiba történt az exportálás során.');
    } finally {
      setExportingDeliveryIds(prev => {
        const next = new Set(prev);
        next.delete(delivery.id);
        return next;
      });
    }
  };

  // Get animal location info
  const getAnimalLocation = (animal: Animal) => {
    if (!animal.current_box_id) return { farm: '-', stable: '-', box: '-' };
    
    const box = boxes.find(b => b.id === animal.current_box_id);
    if (!box) return { farm: '-', stable: '-', box: '-' };
    
    const stable = stables.find(s => s.id === box.stable_id);
    if (!stable) return { farm: '-', stable: '-', box: box.name };
    
    const farm = farms.find(f => f.id === stable.farm_id);
    
    return {
      farm: farm?.name || '-',
      stable: stable.name || '-',
      box: box.name || '-'
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-128px)]">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-3xl font-bold text-gray-900">{t('title', 'shipments')}</h1>
        <p className="mt-2 text-gray-600">
          {t('description', 'shipments')}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-4 flex-shrink-0">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('outgoing')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'outgoing'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('outgoing_shipments', 'shipments')}
          </button>
          <button
            onClick={() => setActiveTab('incoming')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'incoming'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('incoming_shipments', 'shipments')}
          </button>
        </nav>
      </div>

      {/* Controls */}
      <div className="mb-4 flex flex-col sm:flex-row gap-4 items-center justify-between flex-shrink-0">
        <div className="flex gap-4 items-center">
          <ClearableInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Keresés..."
          />

          <ClearableSelect
            value={farmFilter}
            onChange={setFarmFilter}
            placeholder="Minden telep"
          >
            {farms.map((farm) => (
              <option key={farm.id} value={farm.id}>{farm.name}</option>
            ))}
          </ClearableSelect>
        </div>

        <div className="flex gap-3">
          {activeTab === 'incoming' && (
            <button
              onClick={() => navigate('/deliveries/bulk')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Új beszállítás (tömeges)</span>
            </button>
          )}
          <button
            onClick={() => {
              setEditingDelivery(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>{activeTab === 'incoming' ? t('new_incoming_shipment', 'shipments') : t('new_outgoing_shipment', 'shipments')}</span>
          </button>
        </div>
      </div>

      {/* Deliveries Table */}
      <div className="bg-white shadow rounded-lg overflow-auto flex-1 min-h-0 relative">
        {loadingDeliveries && (
          <div className="absolute inset-0 bg-white bg-opacity-60 z-10 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
          </div>
        )}
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-[5]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dátum
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Telep
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Eladó
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('supplier', 'shipments')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Szállítási költség
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('animals_count', 'shipments')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('status', 'deliveries')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Műveletek
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDeliveries.map((delivery) => (
              <tr key={delivery.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(delivery.delivery_date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {delivery.farm?.name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {delivery.seller}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {delivery.transport_company || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(delivery.total_transport_cost)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {delivery.animals?.length || 0}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    delivery.status === 'completed' ? 'bg-green-100 text-green-800' :
                    delivery.status === 'prepared' ? 'bg-yellow-100 text-yellow-800' :
                    delivery.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {t(delivery.status, 'deliveries')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(delivery)}
                      className="text-blue-600 hover:text-blue-900"
                      title={t('edit_shipment', 'shipments')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => openViewAnimalsModal(delivery)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Állatok megtekintése"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleExportDeliveryAnimals(delivery)}
                      disabled={exportingDeliveryIds.has(delivery.id)}
                      className="text-emerald-700 hover:text-emerald-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                      title="Állatok exportálása Excelbe"
                    >
                      {exportingDeliveryIds.has(delivery.id) ? (
                        <div className="w-5 h-5 animate-spin rounded-full border-2 border-emerald-700 border-t-transparent"></div>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => handleOpenDocumentsModal(delivery)}
                      className="text-amber-600 hover:text-amber-900"
                      title="Dokumentumok"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => openContractModal(delivery)}
                      className="text-teal-600 hover:text-teal-900"
                      title="Szerződés generálása"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDelivery(delivery);
                        setShowAnimalModal(true);
                      }}
                      className="text-green-600 hover:text-green-900"
                      title={t('add_animals', 'shipments')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                    {delivery.type === 'outgoing' && delivery.status !== 'completed' && (
                      <button
                        onClick={() => handlePreCheckComplete(delivery.id)}
                        className="text-purple-600 hover:text-purple-900"
                        title={t('mark_completed', 'deliveries')}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmAction({ action: 'deleteDelivery', id: delivery.id })}
                      className="text-red-600 hover:text-red-900"
                      title={t('delete_shipment', 'shipments')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredDeliveries.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">{activeTab === 'incoming' ? t('no_incoming_shipments', 'shipments') : t('no_outgoing_shipments', 'shipments')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {activeTab === 'incoming' ? t('start_new_incoming_shipment', 'shipments') : t('start_new_outgoing_shipment', 'shipments')}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between py-3 flex-shrink-0">
        <span className="text-sm text-gray-600">
          Összesen: {total} szállítás
        </span>
        <Pagination
          page={page}
          lastPage={lastPage}
          onPageChange={setPage}
          disabled={loadingDeliveries}
        />
      </div>

      {/* Add/Edit Delivery Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingDelivery ? t('edit_shipment', 'shipments') : (activeTab === 'incoming' ? t('new_incoming_shipment', 'shipments') : t('new_outgoing_shipment', 'shipments'))}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!editingDelivery && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Telep *
                      </label>
                      <select
                        value={formData.farm_id}
                        onChange={(e) => setFormData({ ...formData, farm_id: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Válassz telepet...</option>
                        {farms.map((farm) => (
                          <option key={farm.id} value={farm.id}>{farm.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {!editingDelivery && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('shipment_type', 'shipments')} *
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as 'incoming' | 'outgoing' })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="incoming">{t('incoming_shipment', 'shipments')}</option>
                        <option value="outgoing">{t('outgoing_shipment', 'shipments')}</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('shipment_date', 'shipments')} *
                    </label>
                    <input
                      type="date"
                      value={formData.delivery_date}
                      onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Eladó *
                    </label>
                    <MasterDataCombobox
                      categoryName="sellers"
                      value={formData.seller}
                      onChange={(val) => setFormData({ ...formData, seller: val })}
                      placeholder="Eladó neve vagy cégnév"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('transport_company', 'shipments')}
                    </label>
                    <MasterDataCombobox
                      categoryName="transport_companies"
                      value={formData.transport_company}
                      onChange={(val) => setFormData({ ...formData, transport_company: val })}
                      placeholder={t('transport_company_name', 'shipments')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Szállítási költség (HUF)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.total_transport_cost}
                      onChange={(e) => setFormData({ ...formData, total_transport_cost: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('invoice_number', 'shipments')}
                    </label>
                    <input
                      type="text"
                      value={formData.invoice_number}
                      onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      placeholder={t('invoice_number_placeholder', 'shipments')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Szerződés szám
                    </label>
                    <input
                      type="text"
                      value={formData.contract_number}
                      onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      placeholder="Szerződés száma"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Megjegyzések
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="További megjegyzések..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingDelivery(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Mégse
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                  >
                    {editingDelivery ? 'Frissítés' : 'Létrehozás'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Animals Modal */}
      <AnimalSelectorModal
        isOpen={showAnimalModal && !!selectedDelivery}
        onClose={() => { setShowAnimalModal(false); setSelectedDelivery(null); }}
        onSelect={handleAnimalSelection}
        mode="multi"
        excludeIds={selectedDelivery?.animals?.map(a => a.id) || []}
        filterByStatus={selectedDelivery?.type === 'outgoing' ? 'aktiv' : undefined}
        showStatusFilter={selectedDelivery?.type === 'outgoing'}
        includeDeliveryWarnings={selectedDelivery?.type === 'outgoing'}
        title={`${t('add_animals_to_shipment', 'shipments')} - ${selectedDelivery?.seller || ''}`}
        confirmLabel={t('add_animals', 'shipments')}
      />

      {/* Warning Confirmation Dialog for adding animals with issues */}
      {showWarningConfirm && pendingAnimals && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex items-start gap-3 mb-4">
              <svg className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.27 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Figyelmeztetések</h3>
                <p className="text-sm text-gray-600 mt-1">A kiválasztott állatok között vannak olyanok, amelyekhez figyelmeztetés tartozik:</p>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto mb-4 space-y-2">
              {pendingAnimals.filter(a => a.delivery_warnings && a.delivery_warnings.length > 0).map(animal => (
                <div key={animal.id} className="border border-yellow-200 rounded-md p-2 bg-yellow-50">
                  <span className="font-medium text-sm">{animal.ear_tag}</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {animal.delivery_warnings!.map((w, i) => {
                      const badge = warningBadge(w);
                      return (
                        <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                          {badge.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => { setShowWarningConfirm(false); setPendingAnimals(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Mégse
              </button>
              <button
                onClick={() => {
                  const ids = pendingAnimals.map(a => a.id);
                  setShowWarningConfirm(false);
                  setPendingAnimals(null);
                  handleAddAnimalsConfirmed(ids);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700"
              >
                Hozzáadás ({pendingAnimals.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Animals Modal */}
      {showViewAnimalsModal && selectedDelivery && (() => {
        const isOutgoingNonCompleted = selectedDelivery.type === 'outgoing' && selectedDelivery.status !== 'completed';
        const animalsWithWarnings = isOutgoingNonCompleted
          ? (selectedDelivery.animals || []).filter((a: any) => a.warnings && a.warnings.length > 0)
          : [];
        const hasBlockingWarnings = animalsWithWarnings.some((a: any) =>
          a.warnings.some((w: AnimalWarning) => w.type === 'already_shipped')
        );

        return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Szállításhoz tartozó állatok - {selectedDelivery.seller}
              </h3>

              {/* Warning summary banner */}
              {isOutgoingNonCompleted && animalsWithWarnings.length > 0 && (
                <div className={`mb-4 p-3 rounded-md border ${hasBlockingWarnings ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center gap-2">
                    <svg className={`h-5 w-5 ${hasBlockingWarnings ? 'text-red-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.27 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className={`text-sm font-medium ${hasBlockingWarnings ? 'text-red-800' : 'text-yellow-800'}`}>
                      {hasBlockingWarnings
                        ? `${animalsWithWarnings.filter((a: any) => a.warnings.some((w: AnimalWarning) => w.type === 'already_shipped')).length} állat már kiszállítva — távolítsd el a befejezéshez.`
                        : `${animalsWithWarnings.length} állatnál figyelmeztetés.`
                      }
                    </span>
                  </div>
                </div>
              )}

              {selectedDelivery.animals && selectedDelivery.animals.length > 0 ? (
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fülszám
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fajta
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Születési dátum
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Telep
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Istálló
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Box
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Státusz
                        </th>
                        {isOutgoingNonCompleted && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Figyelmeztetések
                          </th>
                        )}
                        {selectedDelivery?.status !== 'completed' && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Műveletek
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedDelivery.animals.map((animal) => {
                        const location = getAnimalLocation(animal);
                        const warnings: AnimalWarning[] = (animal as any).warnings || [];
                        return (
                          <tr key={animal.id} className={`hover:bg-gray-50 ${warnings.some(w => w.type === 'already_shipped') ? 'bg-red-50' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {animal.ear_tag}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {animal.breed}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {animal.birth_date ? formatDate(animal.birth_date) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {location.farm}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {location.stable}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {location.box}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                animal.status === 'aktiv' ? 'bg-green-100 text-green-800' :
                                animal.status === 'eladott' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {animal.status}
                              </span>
                            </td>
                            {isOutgoingNonCompleted && (
                              <td className="px-6 py-4 text-sm text-gray-900">
                                {warnings.length > 0 ? (
                                  <div className="flex flex-col gap-1">
                                    {warnings.map((w, i) => {
                                      const badge = warningBadge(w);
                                      return (
                                        <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                                          {badge.label}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : null}
                              </td>
                            )}
                            {selectedDelivery?.status !== 'completed' && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <button
                                  onClick={() => setConfirmAction({ action: 'removeAnimal', id: animal.id, extra: animal.ear_tag })}
                                  className="text-red-600 hover:text-red-900"
                                  title="Állat eltávolítása"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8V9a4 4 0 00-4-4H9a4 4 0 00-4 4v4h14V9z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nincsenek állatok</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Ehhez a szállításhoz még nincsenek hozzáadott állatok.
                  </p>
                </div>
              )}

              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  Összesen: {selectedDelivery.animals?.length || 0} állat
                </p>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => handleExportDeliveryAnimals(selectedDelivery)}
                    disabled={exportingDeliveryIds.has(selectedDelivery.id)}
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-700 border border-transparent rounded-md hover:bg-emerald-800 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {exportingDeliveryIds.has(selectedDelivery.id) ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Exportálás...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Exportálás (Excel)
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowViewAnimalsModal(false);
                      setSelectedDelivery(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Bezárás
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Documents Modal */}
      {showDocumentsModal && selectedDelivery && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Dokumentumok - {selectedDelivery.seller} ({formatDate(selectedDelivery.delivery_date)})
                </h3>
                <button
                  onClick={() => {
                    setShowDocumentsModal(false);
                    setSelectedDelivery(null);
                    setDeliveryDocuments([]);
                    setUploadFile(null);
                    setUploadDocumentType('health_certificate');
                    setUploadDescription('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Document Upload Form */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Új dokumentum feltöltése</h4>
                <form onSubmit={(e) => { e.preventDefault(); handleUploadDocument(); }} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fájl *
                      </label>
                      <input
                        type="file"
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        accept="image/*,.pdf,.doc,.docx,.heic,.heif"
                      />
                      {uploadFile && (
                        <p className="text-sm text-gray-500 mt-1">
                          Kiválasztva: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dokumentum típusa *
                      </label>
                      <select
                        value={uploadDocumentType}
                        onChange={(e) => setUploadDocumentType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="health_certificate">Állategészségügyi Bizonyítvány</option>
                        <option value="livestock_declaration">Állattartói Nyilatkozat</option>
                        <option value="cmr_consignment_note">CMR Fuvarlevél</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Leírás (opcionális)
                    </label>
                    <textarea
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      maxLength={255}
                      placeholder="További megjegyzések a dokumentumról..."
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Feltöltés
                    </button>
                  </div>
                </form>
              </div>

              {/* Existing Documents List */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  Feltöltött dokumentumok ({deliveryDocuments.length})
                </h4>

                {loadingDocuments ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                ) : deliveryDocuments.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {deliveryDocuments.map((doc) => {
                      const documentTypeNames: Record<string, string> = {
                        'health_certificate': 'Állategészségügyi Bizonyítvány',
                        'livestock_declaration': 'Állattartói Nyilatkozat',
                        'cmr_consignment_note': 'CMR Fuvarlevél'
                      };

                      return (
                        <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                          <div className="mb-3">
                            <div className="flex items-start justify-between mb-2">
                              <h5 className="font-medium text-gray-900 text-sm">
                                {documentTypeNames[doc.document_type] || doc.document_type}
                              </h5>
                              <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                                {doc.file_extension}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 truncate" title={doc.original_filename}>
                              {doc.original_filename}
                            </p>
                            {doc.description && (
                              <p className="text-xs text-gray-500 mt-2 italic">{doc.description}</p>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mb-3">
                            {formatDate(doc.created_at)} - {formatFileSize(doc.file_size)}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                              className="flex-1 bg-green-50 text-green-600 px-3 py-2 rounded text-sm hover:bg-green-100 flex items-center justify-center gap-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Letöltés
                            </button>
                            <button
                              onClick={() => setConfirmAction({ action: 'deleteDocument', id: doc.id })}
                              className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm hover:bg-red-100"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nincsenek dokumentumok</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Ehhez a szállításhoz még nincsenek feltöltött dokumentumok.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowDocumentsModal(false);
                    setSelectedDelivery(null);
                    setDeliveryDocuments([]);
                    setUploadFile(null);
                    setUploadDocumentType('health_certificate');
                    setUploadDescription('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Bezárás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Generation Modal */}
      {showContractModal && selectedDelivery && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Szerződés Generálása</h2>
              <button
                onClick={() => {
                  setShowContractModal(false);
                  setSelectedTemplateId(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Szállítás: <strong>{selectedDelivery.seller}</strong> - {selectedDelivery.delivery_date}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Állatok száma: <strong>{selectedDelivery.animals?.length || 0}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Válassz sablont:
                </label>
                {contractTemplates.filter(t => t.is_active && t.template_type === getTemplateType(selectedDelivery.type)).length === 0 ? (
                  <p className="text-sm text-gray-500 italic">
                    Nincs elérhető aktív sablon ehhez a szállítás típushoz. Kérlek tölts fel sablonokat a Szerződés Sablonok oldalon.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {contractTemplates
                      .filter(t => t.is_active && t.template_type === getTemplateType(selectedDelivery.type))
                      .map((template) => (
                        <label
                          key={template.id}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                            selectedTemplateId === template.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="template"
                            value={template.id}
                            checked={selectedTemplateId === template.id}
                            onChange={() => setSelectedTemplateId(template.id)}
                            className="mr-3"
                          />
                          <div className="flex-1">
                            <div className="font-medium">{template.name}</div>
                            {template.description && (
                              <div className="text-sm text-gray-600">{template.description}</div>
                            )}
                          </div>
                        </label>
                      ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowContractModal(false);
                    setSelectedTemplateId(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Mégse
                </button>
                <button
                  onClick={handleGenerateContract}
                  disabled={!selectedTemplateId}
                  className={`px-4 py-2 rounded-md text-white ${
                    selectedTemplateId
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Generálás és Letöltés
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmAction !== null}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
        title={
          confirmAction?.action === 'deleteDelivery' ? t('confirm_delete_delivery', 'deliveries') :
          confirmAction?.action === 'completeDelivery' ? 'Szállítás befejezése' :
          confirmAction?.action === 'deleteDocument' ? 'Dokumentum törlése' :
          confirmAction?.action === 'removeAnimal' ? 'Állat eltávolítása' :
          ''
        }
        message={
          confirmAction?.action === 'deleteDelivery' ? undefined :
          confirmAction?.action === 'completeDelivery' ? 'Biztosan befejezettnek jelöli a szállítást? Az állatok státusza "eladott"-ra változik.' :
          confirmAction?.action === 'deleteDocument' ? 'Biztosan törölni szeretnéd ezt a dokumentumot?' :
          confirmAction?.action === 'removeAnimal' ? `Biztosan eltávolítod ${confirmAction.extra} fülszámú állatot a szállításból?` :
          undefined
        }
        confirmLabel={
          confirmAction?.action === 'completeDelivery' ? 'Befejezés' : 'Törlés'
        }
        variant={confirmAction?.action === 'completeDelivery' ? 'warning' : 'danger'}
      />
    </div>
  );
};

export default Deliveries;